import React, { useState, useEffect, useRef } from 'react'
import Select, { components } from 'react-select'
import { prop } from 'ramda'
import { getRailsEnv } from '../../helpers/rails_helper'

const MultiValueRemove = props => {
  if (props.data.disabled) {
    return null
  }

  return <components.MultiValueRemove {...props}>x</components.MultiValueRemove>
}

const MultiValueLabel = props => {
  if (props.data.disabled) {
    return (
      <span style={{ cursor: 'not-allowed' }}>
        <components.MultiValueLabel {...props} />
      </span>
    )
  }
  return <components.MultiValueLabel {...props} />
}

const ClearIndicator = props => {
  // Get the current selected values from props
  const selectedValues = props.selectProps.value || []
  // Check if there is at least one non-disabled value
  const hasEnabled = selectedValues.some(v => !v.disabled)
  if (!hasEnabled) return null
  return <components.ClearIndicator {...props} />
}

const MultiSelect = ({
  inputName,
  options,
  selectedOptions,
  placeholder = 'Select...',
  allowsOnlyOneSelection = false,
  menuPlacement = 'auto',
  menuPosition = 'absolute',
  errorMessage = null,
  disabled = false,
  inputId
}) => {
  const label = value => `${value.name}${value.exclusive ? ' - Exclusive' : ' '}`
  const preSelectedValues = selectedOptions.map(value => ({
    label: label(value),
    value: value.id,
    exclusive: value.exclusive,
    disabled: value.disabled || false
  }))
  const allowsOnlyOneDisability = values => {
    return values && values.length === 1
  }
  const exclusiveDisability = (value, selectedValues) => {
    if (selectedValues.length === 0) return false
    const exclusiveValueIsSelected = selectedValues.map(prop('exclusive')).includes(true)
    return (!exclusiveValueIsSelected && value.exclusive === true) || exclusiveValueIsSelected
  }
  const isDisabled = (value, values) => {
    return allowsOnlyOneSelection
      ? allowsOnlyOneDisability(values || preSelectedValues)
      : exclusiveDisability(value, values || preSelectedValues || [])
  }
  const allValues = options.map(value => ({
    label: label(value),
    value: value.id,
    exclusive: value.exclusive,
    disabled: isDisabled(value) || value.disabled
  }))

  // Helper function to sort values with disabled items first
  const sortValuesWithDisabledFirst = values => {
    return values.sort((a, b) => {
      if (a.disabled && !b.disabled) return -1
      if (!a.disabled && b.disabled) return 1
      return 0
    })
  }

  const [state, setState] = useState(() => {
    return {
      selectableValues: sortValuesWithDisabledFirst(preSelectedValues),
      allValues: allValues
    }
  })

  const firstLoad = useRef(true)
  useEffect(() => {
    if (!firstLoad.current) {
      var customEvent = new CustomEvent('input:change', {
        detail: {
          name: inputName,
          selectedIds: state.selectableValues.map(value => value.value)
        }
      })
      window.dispatchEvent(customEvent)
    } else {
      firstLoad.current = false
    }
  }, [state])

  const onChange = (values, actionMeta) => {
    if (actionMeta.action === 'clear') {
      // Only keep disabled items
      const disabledValues = state.selectableValues.filter(v => v.disabled)
      setState(state => ({ ...state, selectableValues: disabledValues, allValues: state.allValues }))
    } else {
      state.allValues.forEach(value => {
        value.disabled = isDisabled(value, values)
      })
      // Sort values so disabled items appear first
      const sortedValues = sortValuesWithDisabledFirst(values)
      setState(state => ({ ...state, selectableValues: sortedValues, allValues: state.allValues }))
    }
  }

  const onKeyDown = event => {
    // Prevent backspace from removing disabled items
    if (event.key === 'Backspace') {
      console.log('Hello there', state.selectableValues)
      const focusedValue = state.selectableValues[state.selectableValues.length - 1]?.disabled
      if (focusedValue) {
        event.preventDefault()
        return false
      }
    }
  }

  const customStyles = {
    control: (provided, state) => {
      let border = errorMessage ? 'solid 1px #f95a53' : state.isFocused ? 'solid 1px #2684FF' : 'solid 1px #DDDDDD'

      return {
        ...provided,
        border: border,
        '&:hover': {
          border: border
        }
      }
    },
    option: (provided, state) => ({
      ...provided,
      ':hover': {
        backgroundColor: '#edf0ff'
      }
    }),
    singleValue: (provided, state) => {
      const opacity = state.exclusiveDisability ? 0.5 : 1
      const transition = 'opacity 300ms'

      return { ...provided, opacity, transition }
    },
    multiValue: (styles, { data }) => {
      return {
        ...styles,
        backgroundColor: '#edf0ff'
      }
    }
  }

  return (
    <>
      <Select
        value={state.selectableValues || []}
        options={state.allValues || []}
        onChange={onChange}
        onKeyDown={onKeyDown}
        isOptionDisabled={option => option.disabled === true}
        placeholder={placeholder}
        isMulti
        styles={customStyles}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition} // Added to prevent fixed menus from scrolling out of their containers and messing with layouts. Native browser select captures scroll so this just brings the React Select in line
        menuShouldBlockScroll={getRailsEnv() === 'test' ? false : true} // Scroll blocker intercepts Selenium clicks, so we disable in test mode
        isDisabled={disabled}
        inputId={inputId}
        components={{ MultiValueRemove, MultiValueLabel, ClearIndicator }}
      />
      {state.selectableValues && state.selectableValues.length > 0 ? (
        <>
          {state.selectableValues.map(value => (
            <input
              key={value.value}
              name={`${inputName}[]`}
              className='form-control hidden'
              type='hidden'
              value={value.value}
            />
          ))}
        </>
      ) : (
        <input key={0} name={`${inputName}[]`} className='form-control hidden' type='hidden' value={''} />
      )}
      {errorMessage && <div className='d-block invalid-feedback'>{errorMessage}</div>}
    </>
  )
}

export default MultiSelect
